import React, { useState } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const MARKETPLACES = [
  { value: '', label: 'All Marketplaces' },
  { value: 'US', label: 'United States (US)' },
  { value: 'UK', label: 'United Kingdom (UK)' },
  { value: 'DE', label: 'Germany (DE)' },
  { value: 'FR', label: 'France (FR)' },
  { value: 'IT', label: 'Italy (IT)' },
  { value: 'ES', label: 'Spain (ES)' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'JP', label: 'Japan (JP)' },
];

function CategorySearch({ onCategoriesUpdate, setLoading }) {
  const [marketplace, setMarketplace] = useState('');
  const [nodeIdPath, setNodeIdPath] = useState('');
  const [keyword, setKeyword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const params = {};

      if (marketplace) params.marketplace = marketplace;
      if (nodeIdPath) params.nodeIdPath = nodeIdPath;
      if (keyword) params.keyword = keyword;

      const response = await axios.get('/api/categories', { params });

      if (response.data.code === 'OK' && response.data.data) {
        onCategoriesUpdate(response.data.data);
        setSuccess(`Successfully fetched ${response.data.data.length} scraped categories from database`);
      } else {
        setError(response.data.message || 'Query failed');
        onCategoriesUpdate([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Query failed, please check database connection');
      onCategoriesUpdate([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMarketplace('');
    setNodeIdPath('');
    setKeyword('');
    onCategoriesUpdate([]);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
        Scraped Categories Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage categories that have been scraped from Amazon. Use filters below to search for specific categories.
      </Typography>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Marketplace</InputLabel>
              <Select
                value={marketplace}
                label="Marketplace"
                onChange={(e) => setMarketplace(e.target.value)}
              >
                {MARKETPLACES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Category Node ID"
              value={nodeIdPath}
              onChange={(e) => setNodeIdPath(e.target.value)}
              placeholder="3408401"
              helperText="Filter by Amazon node ID"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Dumbbells"
              helperText="Search in category name"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                type="submit"
                fullWidth
              >
                Search Scraped Categories
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ minWidth: 120 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default CategorySearch;
